import { Button } from "@/components/ui/button";
import { Check, X, MessageCircle, GraduationCap, ShieldCheck, Stethoscope, Users, AlertTriangle, MapPin } from "lucide-react";
import { useT, WHATSAPP_NUMBER, WHATSAPP_DISPLAY } from "@/lib/i18n";

export default function Trainer() {
  const t = useT();
  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Bonjour Haithem, je suis intéressé par Mazra3ti."
  )}`;

  return (
    <section
      id="trainer"
      data-testid="trainer-section"
      className="py-24 md:py-32 bg-[#FAFAF7] relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-1/2 -translate-y-1/2 -left-32 w-[420px] h-[420px] rounded-full opacity-[0.05] bg-[radial-gradient(#1C3A27_1px,transparent_1px)] [background-size:14px_14px]"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center relative">
        {/* Avatar + brand chip */}
        <div className="lg:col-span-5">
          <div className="relative max-w-md mx-auto lg:mx-0">
            <div
              aria-hidden
              className="absolute -inset-3 rounded-[2rem] bg-[#E05A3D]/15 -rotate-3"
            />
            <div className="relative rounded-[2rem] overflow-hidden bg-[#F0E4D2] border border-[#E2E4DF] shadow-xl">
              <img
                src="/haithem-avatar.jpg"
                alt="Haithem Sakkal, fondateur Mazra3ti"
                data-testid="trainer-avatar"
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Brand chip */}
            <div
              data-testid="trainer-school-chip"
              className="absolute -bottom-5 left-6 right-6 bg-white rounded-2xl border border-[#E2E4DF] p-4 shadow-lg flex items-center gap-3"
            >
              <div className="w-11 h-11 rounded-xl bg-[#1C3A27] grid place-items-center shrink-0">
                <GraduationCap size={20} className="text-[#E05A3D]" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[#5C6B5C] font-semibold">
                  {t.trainer.badge}
                </div>
                <div className="text-sm text-[#1C3A27] font-bold truncate">
                  {t.trainer.school_full}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-7 pt-10 lg:pt-0">
          <div className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
            <ShieldCheck size={16} />
            {t.trainer.overline}
          </div>

          <h2 className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tight text-[#1C3A27] leading-[1.1]">
            {t.trainer.headline_a}{" "}
            <span className="marker-accent italic">{t.trainer.headline_b}</span>{" "}
            {t.trainer.headline_c}
          </h2>

          <p className="mt-6 text-lg text-[#5C6B5C] leading-relaxed max-w-2xl">
            {t.trainer.description}
          </p>

          <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3.5 max-w-2xl">
            {t.trainer.perks.map((p, i) => (
              <li
                key={i}
                data-testid={`trainer-perk-${i}`}
                className="flex items-start gap-3"
              >
                <span className="mt-0.5 w-5 h-5 rounded-full bg-[#1C3A27] grid place-items-center shrink-0">
                  <Check size={12} strokeWidth={3} className="text-white" />
                </span>
                <span className="text-[#1C3A27] text-base">{p}</span>
              </li>
            ))}
          </ul>

          {/* Formations & compétences */}
          {t.trainer.competencies && t.trainer.competencies.length > 0 && (
            <div
              data-testid="trainer-education-block"
              className="mt-8 rounded-2xl border border-[#E2E4DF] bg-white/60 backdrop-blur p-5 max-w-2xl"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#1C3A27] grid place-items-center shrink-0">
                  <GraduationCap size={18} className="text-[#E05A3D]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-[#5C6B5C] font-semibold">
                    {t.trainer.education_title}
                  </div>
                  <div className="text-sm font-bold text-[#1C3A27] mt-0.5">
                    {t.trainer.education_school}
                  </div>
                  <ul
                    data-testid="trainer-competencies"
                    className="mt-3 flex flex-wrap gap-2"
                  >
                    {t.trainer.competencies.map((c, i) => (
                      <li
                        key={i}
                        className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#F0F0EB] text-[#1C3A27] text-xs font-medium"
                      >
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button
              asChild
              data-testid="trainer-whatsapp-btn"
              className="h-13 px-6 rounded-2xl bg-[#25D366] hover:bg-[#1FAD52] text-white font-semibold text-base h-13"
            >
              <a href={waLink} target="_blank" rel="noopener noreferrer">
                <MessageCircle size={18} className="me-2" />
                {t.trainer.cta_whatsapp}
              </a>
            </Button>
            <a
              href="#pricing"
              data-testid="trainer-cta-pricing"
              className="text-sm font-semibold text-[#1C3A27] hover:text-[#E05A3D] underline underline-offset-4 decoration-[#E05A3D]"
            >
              {t.pricing.overline} →
            </a>
          </div>

          <p
            data-testid="trainer-whatsapp-number"
            className="mt-4 text-xs text-[#5C6B5C]"
          >
            WhatsApp&nbsp;:{" "}
            <span dir="ltr" className="font-medium text-[#1C3A27]">
              {WHATSAPP_DISPLAY}
            </span>
          </p>
        </div>
      </div>

      {/* Team section — agronomic & animal engineer */}
      {t.trainer.team_members && t.trainer.team_members.length > 0 && (
        <div
          data-testid="trainer-team-section"
          className="max-w-7xl mx-auto px-6 md:px-12 mt-24 md:mt-32"
        >
          <div className="dashed-divider mb-12" />
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
                <Users size={16} />
                {t.trainer.team_overline}
              </div>
              <h3 className="mt-4 font-display text-3xl md:text-4xl font-black text-[#1C3A27] leading-[1.15]">
                {t.trainer.team_title}
              </h3>
              <p className="mt-5 text-base text-[#5C6B5C] leading-relaxed">
                {t.trainer.team_desc}
              </p>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-1 gap-5">
              {t.trainer.team_members.map((m, i) => (
                <div
                  key={i}
                  data-testid={`team-member-${i}`}
                  className="relative rounded-3xl border border-[#E2E4DF] bg-white p-6 md:p-7 hover:border-[#1C3A27]/40 transition-colors"
                >
                  <div className="flex items-start gap-5">
                    {m.photo ? (
                      <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden shrink-0 ring-2 ring-[#E05A3D]/25 shadow-md">
                        <img
                          src={m.photo}
                          alt={m.name}
                          data-testid={`team-member-${i}-photo`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-2xl bg-[#1C3A27] grid place-items-center text-[#FAFAF7] font-display font-black text-xl shrink-0 shadow-md">
                        {m.initials}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-display text-xl font-bold text-[#1C3A27]">
                          {m.name}
                        </h4>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFE8DC] text-[#E05A3D] text-[10px] font-bold uppercase tracking-wider">
                          <Stethoscope size={10} />
                          Expert
                        </span>
                      </div>
                      <p className="mt-1 text-sm font-semibold text-[#5C6B5C]">
                        {m.role}
                      </p>
                      <p className="mt-3 text-sm text-[#1C3A27]/80 leading-relaxed">
                        {m.bio}
                      </p>
                      {m.education && m.education.length > 0 && (
                        <ul
                          data-testid={`team-member-${i}-education`}
                          className="mt-4 flex flex-wrap gap-2"
                        >
                          {m.education.map((edu, j) => (
                            <li
                              key={j}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F0F0EB] text-[#1C3A27] text-xs font-medium"
                            >
                              <GraduationCap size={12} className="text-[#E05A3D]" />
                              {edu}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Scope: what Seifeddine handles / does NOT handle */}
                      {(m.scope_in || m.scope_out) && (
                        <div className="mt-5 grid sm:grid-cols-2 gap-4 pt-4 border-t border-[#E2E4DF]">
                          {m.scope_in && (
                            <div data-testid={`team-member-${i}-scope-in`}>
                              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold mb-2">
                                Conseille sur
                              </div>
                              <ul className="space-y-1.5">
                                {m.scope_in.map((s, k) => (
                                  <li key={k} className="flex items-start gap-2 text-xs text-[#1C3A27]">
                                    <Check size={13} className="text-emerald-600 mt-0.5 shrink-0" strokeWidth={3} />
                                    {s}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {m.scope_out && (
                            <div data-testid={`team-member-${i}-scope-out`}>
                              <div className="text-[10px] uppercase tracking-widest text-rose-700 font-bold mb-2">
                                Ne traite pas
                              </div>
                              <ul className="space-y-1.5">
                                {m.scope_out.map((s, k) => (
                                  <li key={k} className="flex items-start gap-2 text-xs text-[#5C6B5C]">
                                    <X size={13} className="text-rose-500 mt-0.5 shrink-0" strokeWidth={3} />
                                    {s}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Global legal disclaimer */}
              {t.trainer.team_disclaimer && (
                <div
                  data-testid="team-legal-disclaimer"
                  className="rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3 text-xs leading-relaxed text-amber-900"
                >
                  <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                  <p>{t.trainer.team_disclaimer}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
